import React from 'react';
import { useReducedMotion, usePointerParallax } from '../hooks';
import { useRipples, RippleLayer } from './useRipples';
import './worlds.css';

/**
 * FlowingWaves — layered gradient ribbons flow continuously and drift gently with
 * the pointer (desktop); a calm central ring breathes in a slow, regular rhythm.
 * Tap anywhere for a soft ripple. Deliberately smooth and unhurried — no sharp or
 * sudden motion. Static and ripple-only under reduced motion.
 */
export const FlowingWaves: React.FC = () => {
  const reduced = useReducedMotion();
  const parallaxRef = usePointerParallax<HTMLDivElement>();
  const { ripples, spawnAt, spawnAtElement } = useRipples();

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => spawnAt(e.currentTarget, e.clientX, e.clientY);
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      spawnAtElement(e.currentTarget);
    }
  };

  return (
    <div
      ref={parallaxRef}
      className={`mm-world mm-world--waves ${reduced ? 'is-still' : ''}`}
      role="img"
      aria-label="Flowing waves — tap for a gentle ripple"
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    >
      <svg className="mm-waves__svg" viewBox="0 0 400 220" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="mm-wave-g1" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="var(--mood-accent, #a98bd6)" stopOpacity="0.5" />
            <stop offset="1" stopColor="var(--mood-soft, #e2d6fb)" stopOpacity="0.5" />
          </linearGradient>
          <linearGradient id="mm-wave-g2" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="var(--mood-soft, #e2d6fb)" stopOpacity="0.6" />
            <stop offset="1" stopColor="var(--mood-accent, #a98bd6)" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        <path className="mm-ribbon mm-ribbon--1" fill="url(#mm-wave-g1)" d="M0 120 q 100 -44 200 0 t 200 0 V220 H0 Z" />
        <path className="mm-ribbon mm-ribbon--2" fill="url(#mm-wave-g2)" d="M0 150 q 100 -34 200 0 t 200 0 V220 H0 Z" />
        <path className="mm-ribbon mm-ribbon--3" fill="var(--mood-accent, #a98bd6)" fillOpacity="0.25" d="M0 180 q 100 -26 200 0 t 200 0 V220 H0 Z" />
      </svg>
      <span className="mm-waves__core" aria-hidden="true" />
      <RippleLayer ripples={ripples} />
      <p className="mm-world__hint">Breathe with the flow — tap for a ripple</p>
    </div>
  );
};

export default FlowingWaves;
